/**
 * Market Data WebSocket Singleton Service
 * Manages the single multiplexed persistent connection to market-data-service.
 */
import { Quote, updateQuotes } from '../store/slices/marketDataSlice';
import { store } from '../store';

// Default to localhost:8002 if environment variable is not set
const WS_URL = import.meta.env.VITE_MARKET_DATA_WS_URL || 'wss://market-data-service-171283474101.asia-south1.run.app/api/v1/stream';

class MarketDataWebSocket {
    private static instance: MarketDataWebSocket;
    private ws: WebSocket | null = null;
    private activeSubscriptions: Set<string> = new Set();
    private isConnecting: boolean = false;
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 5;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    private constructor() { }

    public static getInstance(): MarketDataWebSocket {
        if (!MarketDataWebSocket.instance) {
            MarketDataWebSocket.instance = new MarketDataWebSocket();
        }
        return MarketDataWebSocket.instance;
    }

    public connect() {
        if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
            return;
        }

        this.isConnecting = true;
        console.log(`[MarketDataWS] Connecting to ${WS_URL}...`);

        try {
            this.ws = new WebSocket(WS_URL);

            this.ws.onopen = this.handleOpen.bind(this);
            this.ws.onmessage = this.handleMessage.bind(this);
            this.ws.onclose = this.handleClose.bind(this);
            this.ws.onerror = this.handleError.bind(this);
        } catch (error) {
            console.error('[MarketDataWS] Connection error:', error);
            this.isConnecting = false;
            this.scheduleReconnect();
        }
    }

    public disconnect() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }

        this.activeSubscriptions.clear();
        this.reconnectAttempts = 0;
    }

    public subscribe(tokens: string[]) {
        if (!tokens || tokens.length === 0) return;

        // Keep track of what we want to be subscribed to, regardless of connection state
        const newTokens = tokens.filter(t => !this.activeSubscriptions.has(t));

        tokens.forEach(t => this.activeSubscriptions.add(t));

        if (newTokens.length > 0) {
            if (this.ws?.readyState === WebSocket.OPEN) {
                console.log(`[MarketDataWS] Subscribing to ${newTokens.length} new tokens:`, newTokens);
                this.ws.send(JSON.stringify({
                    action: 'subscribe',
                    tokens: newTokens
                }));
            } else {
                // WS not yet open — tokens are already tracked in activeSubscriptions.
                // handleOpen() will flush ALL activeSubscriptions when the connection is established.
                console.log(`[MarketDataWS] WS not open (state=${this.ws?.readyState ?? 'null'}) — ${newTokens.length} tokens queued, will be sent on open:`, newTokens);
            }
        }
    }

    public unsubscribe(tokens: string[]) {
        if (!tokens || tokens.length === 0) return;

        tokens.forEach(t => this.activeSubscriptions.delete(t));

        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                action: 'unsubscribe',
                tokens: tokens
            }));
        }
    }

    private handleOpen() {
        console.log('[MarketDataWS] Connected.');
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Clear any pending reconnect timer now that we are live.
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        // Re-subscribe to everything we care about
        if (this.activeSubscriptions.size > 0) {
            const tokens = Array.from(this.activeSubscriptions);
            console.log(`[MarketDataWS] Resending subscription for ${tokens.length} tokens`);
            this.ws?.send(JSON.stringify({
                action: 'subscribe',
                tokens: tokens
            }));
        }
    }

    private handleMessage(event: MessageEvent) {
        try {
            const message = JSON.parse(event.data);
            if (message.type === 'ping') {
                // Respond to server keepalive pings so Cloud Run does not drop the connection.
                this.ws?.send(JSON.stringify({ type: 'pong' }));
                return;
            }
            if (message.type === 'quote' && message.data) {
                // Dispatch raw quote data directly to Redux store
                console.log(`[MarketDataWS] Received quote for ${message.data.instrument_token}:`, message.data);
                store.dispatch(updateQuotes([message.data as Quote]));
            }
        } catch (error) {
            console.error('[MarketDataWS] Failed to parse message:', error);
        }
    }

    private handleClose(event: CloseEvent) {
        console.log(`[MarketDataWS] Closed (code: ${event.code})`);
        this.ws = null;
        this.isConnecting = false;
        this.scheduleReconnect();
    }

    private handleError(event: Event) {
        console.error('[MarketDataWS] Error:', event);
    }

    private scheduleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('[MarketDataWS] Max reconnect attempts reached. Giving up.');
            return;
        }

        const backoffMs = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
        console.log(`[MarketDataWS] Reconnecting in ${backoffMs}ms...`);

        this.reconnectTimeout = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
        }, backoffMs);
    }
}

export const marketDataWS = MarketDataWebSocket.getInstance();
